import { Close, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import { useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import ProtectedPermissionView from "../../components/ProtectedPermissionView";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import facilityService from "../../services/facility";
import config from "../../utils/config";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function FacilityList(props: any) {
  const [pageSize, setPageSize] = useState<number>(10);
  const [options, setOptions] = useState<any>({
    keyword: "",
  });
  const notification = useNotification();
  const deleteFacility = useMutation("delete-facility", facilityService.deleteFacility, {
    onSuccess: () => {
      listFacility.refetch();
      notification.onOpen({
        message: "Faclity was successfully deleted!",
        type: "success",
        position: "top",
      });
    },
    onError: (error: any) => {
      listFacility.refetch();
      notification.onOpen({
        message:
          error?.response?.data?.message === undefined
            ? "Something went wrong"
            : error?.response?.data?.message,
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });
  const onDelete = (id: number) => {
    alert.onClose();
    deleteFacility.mutate(id);
  };
  const alert = useAlert();
  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure want to delete this facility?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };
  const listFacility = useQuery(
    ["facility-list", options],
    ({ queryKey }) => {
      return facilityService.getAllFacility({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  const form = useForm({
    defaultValues: {
      keyword: "",
    },
  });

  const onHandleSearch = useCallback((values: { keyword: any }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);
  return (
    <MainLayout>
      <Helmet>
        <title>Facility List</title>
      </Helmet>

      <Breadcrumb
        label={`Facility List`}
        breadcrumbs={[
          { label: "Facility Management", href: "/app/properties/facility" },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="facility"
        requiredPermissions={{ is_read: true }}
      >
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <Controller
              name="keyword"
              control={form.control}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Input Keyword here"
                  {...field}
                  {...config.onEnter(() => {
                    form.handleSubmit(onHandleSearch)();
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        onClick={() => {
                          if (field.value) {
                            field.onChange("");
                            setOptions((prev: any) => ({
                              ...prev,
                              keyword: "",
                            }));
                          }
                        }}
                      >
                        {field.value ? (
                          <Close color="error" sx={{ cursor: "pointer" }} />
                        ) : (
                          <Search />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              variant="outlined"
              onClick={form.handleSubmit(onHandleSearch)}
            >
              SEARCH
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <ProtectedPermissionView
            requiredPermissions={{ is_create: true }}
            menuSlug="facility"
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                component={RouterLink}
                to="/app/properties/facility/create"
              >
                ADD FACILITY
              </Button>
            </Stack>
          </ProtectedPermissionView>
        </Box>

        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={listFacility.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={get(listFacility, "data.data", [])}
            rowCount={get(listFacility, "data.data", []).length}
            page={options.page_number}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageChange={(newPage) => {
              setOptions((prev: any) => ({ ...prev, page_number: newPage }));
            }}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              {
                field: "name",
                headerName: "Name",
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "logo",
                headerName: "Logo",
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "type",
                headerName: "Type",
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "actions",
                headerName: "Action",
                sortable: false,
                filterable: false,
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack direction="row" spacing={1}>
                      <ProtectedPermissionView
                        menuSlug="facility"
                        requiredPermissions={{ is_update: true }}
                      >
                        <Button
                          variant="outlined"
                          color="primary"
                          component={RouterLink}
                          to={`/app/properties/facility/edit/${get(
                            params,
                            "row.id"
                          )}`}
                        >
                          Edit
                        </Button>
                      </ProtectedPermissionView>

                      <ProtectedPermissionView
                        menuSlug="facility"
                        requiredPermissions={{ is_delete: true }}
                      >
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => onClickDelete(params.row.id)}
                        >
                          Delete
                        </Button>
                      </ProtectedPermissionView>
                    </Stack>
                  );
                },
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
